import type { Annotation } from "@rapid/types/annotation";
import { PromptingData } from "./types";
import { MediaItem, MediaItemStatus } from "@rapid/types/media";
import {
    buildBoxPromptingData,
    calculateBoxPromptConfidenceThreshold
} from "@rapid/utils/predictions";

/**
 * Build box prompting data for labels that have user-created or user-adjusted boxes.
 * This is a wrapper around buildBoxPromptingData that handles the nested annotation structure.
 *
 * @param annotations - All annotations across all files/frames (nested Record structure)
 * @param labels - Labels to include in box prompting
 * @param mediaDimensions - Map of frameId to dimensions
 * @param workspaceUrl - Workspace URL prefix
 * @param maxPromptsPerClass - Maximum prompts per class (default: 5)
 * @returns Object containing box prompting data and calculated confidence threshold
 */
export function buildBoxPromptingDataForLabels(
    annotations: Record<string, Record<string, Annotation[]>>,
    labels: string[],
    mediaDimensions: Map<string, { width: number; height: number }>,
    workspaceUrl: string,
    maxPromptsPerClass?: number
): { promptingData: PromptingData[]; confidenceThreshold: number } {
    if (labels.length === 0) {
        return { promptingData: [], confidenceThreshold: 0 };
    }

    // Flatten nested annotations structure into array with frameId/fileId
    const flatAnnotations: (Annotation & { frameId: string; fileId: string })[] = [];

    Object.entries(annotations).forEach(([fileId, frames]) => {
        Object.entries(frames).forEach(([frameId, frameAnnotations]) => {
            frameAnnotations.forEach((anno) => {
                // Only user-created or user-adjusted boxes become box prompts.
                // Model predictions keep confidence and are intentionally excluded.
                if (!anno.draft && anno.confidence == null && labels.includes(anno.label)) {
                    flatAnnotations.push({ ...anno, frameId, fileId });
                }
            });
        });
    });

    if (flatAnnotations.length === 0) {
        return { promptingData: [], confidenceThreshold: 0 };
    }

    // Use the centralized buildBoxPromptingData function
    const promptingData = buildBoxPromptingData(flatAnnotations, mediaDimensions, {
        workspaceUrl,
        maxPromptsPerClass
    });

    // Calculate total prompt count for confidence threshold
    const totalPromptCount = promptingData.reduce((sum, data) => sum + data.boxes.length, 0);
    const confidenceThreshold = calculateBoxPromptConfidenceThreshold(totalPromptCount);

    return { promptingData, confidenceThreshold };
}

/**
 * Build custom prompting data from annotations
 */
export function buildPromptingData(
    annotations: Annotation[],
    mediaItem: MediaItem,
    frameId: string,
    workspaceUrl: string
): PromptingData[] {
    // Filter out draft annotations
    const nonDraftAnnotations = annotations.filter((anno) => !anno.draft);

    if (nonDraftAnnotations.length === 0) {
        return [];
    }

    const width =
        mediaItem.metadata?.naturalDimensions?.width ||
        mediaItem.metadata?.serverMediaDimensions?.width ||
        0;

    const height =
        mediaItem.metadata?.naturalDimensions?.height ||
        mediaItem.metadata?.serverMediaDimensions?.height ||
        0;

    return [
        {
            file_uuid: `${workspaceUrl}/${frameId}`,
            width,
            height,
            boxes: nonDraftAnnotations.map((annotation) => ({
                x: Math.round(annotation.x),
                y: Math.round(annotation.y),
                w: Math.round(annotation.width),
                h: Math.round(annotation.height),
                negative: false,
                cls: annotation.label || "object"
            }))
        }
    ];
}

/**
 * Validate if prompting data has valid boxes
 */
export function hasValidPromptingData(promptingData: PromptingData[]): boolean {
    return (
        promptingData.length > 0 &&
        promptingData[0].boxes.length > 0 &&
        promptingData[0].boxes.every((box) => box.cls && box.cls !== "")
    );
}

/**
 * Create a function to mark annotations as draft
 * This returns a function that can be used with setState
 */
export function createMarkAnnotationsAsDraft(fileId: string, frameId: string) {
    return (prev: Record<string, Record<string, Annotation[]>>) => {
        const current = prev[fileId] || {};
        current[frameId] = current[frameId]?.map((anno) => ({ ...anno, draft: true })) || [];

        return { ...prev, [fileId]: current };
    };
}

/**
 * Get unique frameIds from box prompting data.
 * These are the frames that will be used as visual prompts for OWLv2 inference.
 */
export function getPromptFrameIds(boxPromptingData: PromptingData[]): string[] {
    // file_uuid format is "workspaceUrl/frameId", we need to extract the frameId
    return boxPromptingData.map((data) => {
        const parts = data.file_uuid.split("/");
        return parts[parts.length - 1]; // Get the last part (frameId)
    });
}

/**
 * Wait for all prompt frames to be uploaded before running inference.
 * This prevents "Data is not image" errors when the backend tries to fetch
 * frames that are still being uploaded.
 *
 * @param promptFrameIds - Frame IDs that will be used as prompts
 * @param getMedia - Function to get fresh media state (called each poll iteration)
 * @param maxWaitMs - Maximum time to wait for uploads (default: 30000ms)
 * @param pollIntervalMs - Interval between status checks (default: 500ms)
 * @returns Promise that resolves when all frames are uploaded, or rejects on timeout/error
 */
export async function waitForPromptFrameUploads(
    promptFrameIds: string[],
    getMedia: () => Record<string, MediaItem>,
    maxWaitMs: number = 30000,
    pollIntervalMs: number = 500
): Promise<void> {
    if (promptFrameIds.length === 0) {
        return;
    }

    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitMs) {
        // Get fresh media state each iteration
        const media = getMedia();

        const pendingFrames: string[] = [];
        const errorFrames: string[] = [];
        const missingFrames: string[] = [];

        for (const frameId of promptFrameIds) {
            const frameMediaItem = media[frameId];

            if (!frameMediaItem) {
                missingFrames.push(frameId);
                continue;
            }

            if (frameMediaItem.status === MediaItemStatus.ERROR) {
                errorFrames.push(frameId);
            } else if (frameMediaItem.status !== MediaItemStatus.COMPLETED) {
                pendingFrames.push(frameId);
            }
        }

        // If any frames have errors, fail immediately
        if (errorFrames.length > 0) {
            throw new Error(`Prompt frame upload failed for frames: ${errorFrames.join(", ")}`);
        }

        // If all frames are either completed or missing (not in media store), proceed
        // Missing frames might be images that don't need upload or are already uploaded
        if (pendingFrames.length === 0) {
            return;
        }

        // Wait before checking again
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    // Timeout reached
    throw new Error(`Timeout waiting for prompt frame uploads after ${maxWaitMs}ms`);
}
